// Owner Panel theme in the admin panel, at runtime (port of tenexch admin/src/siteBrand.js).
// The super owner's tokens are the PLAYER site's (server helper/siteSettings.js);
// the admin takes the header / sub-header ones, and its primary buttons follow the
// header colour once one is set, so an untouched site keeps today's admin exactly.
// Logo, favicon and name replace the built-in ones. This admin has no socket, so a
// save shows here on the next page load.
// Plan: docs/plans/2026-10-28-owner-panel.md
import { useSyncExternalStore } from 'react'
import { API_BASE_URL } from './config/api'

// Its own key: the player site's cache (`cf:theme`) has another shape.
const CACHE_KEY = 'cf:theme:admin'
const SHADES = {
  '--bg-primary': { '--bg-primary90': 'e6' },
  '--bg-secondary': { '--bg-secondary70': 'b3', '--bg-secondary85': 'd9' },
}
const ADMIN_TOKENS = ['--bg-primary', '--text-primary', '--bg-secondary', '--text-secondary']
const hexA = (c, a) => (/^#[0-9a-f]{6}$/i.test(c || '') ? c + a : '')

// Cockpit-set defaults (Docker: /env.js → window.__APP_ENV__ APP_NAME / APP_LOGO /
// APP_FAVICON). They replace the built-in look until the owner uploads a theme.
const rt = (typeof window !== 'undefined' && window.__APP_ENV__) || {}
const rtv = (k) => (rt[k] != null && String(rt[k]).trim() !== '' ? String(rt[k]) : '')
const ENV_BRAND = { logo: rtv('APP_LOGO'), favicon: rtv('APP_FAVICON'), name: rtv('APP_NAME') }
// Cockpit colours (same tokens the owner theme uses); the owner theme wins where set.
const ENV_COLORS = Object.fromEntries(Object.entries({
  '--bg-primary': 'APP_COLOR_PRIMARY', '--text-primary': 'APP_COLOR_PRIMARY_TEXT',
  '--bg-secondary': 'APP_COLOR_SECONDARY', '--text-secondary': 'APP_COLOR_SECONDARY_TEXT',
}).map(([k, e]) => [k, rtv(e)]).filter(([, v]) => /^#[0-9a-f]{6}$/i.test(v)))
let brand = { ...ENV_BRAND } // '' = the built-in one
let painted = []
const listeners = new Set()
const subscribe = (cb) => { listeners.add(cb); return () => listeners.delete(cb) }

/** { logo, favicon, name } — '' where the built-in one applies; re-renders when the theme arrives. */
export const useSiteBrand = () => useSyncExternalStore(subscribe, () => brand)

function paint({ colors: themeColors = {}, name = '', logo = '', favicon = '' }) {
  const colors = { ...ENV_COLORS, ...Object.fromEntries(Object.entries(themeColors || {}).filter(([, v]) => v)) }
  const style = document.documentElement.style
  painted.forEach((k) => style.removeProperty(k))
  const vars = ADMIN_TOKENS.filter((k) => colors[k]).flatMap((k) => [
    [k, colors[k]],
    ...Object.entries(SHADES[k] || {}).map(([shade, a]) => [shade, hexA(colors[k], a)]),
  ])
  if (colors['--bg-primary']) vars.push(['--btn-primary', colors['--bg-primary']])
  vars.filter(([, v]) => v).forEach(([k, v]) => style.setProperty(k, v))
  painted = vars.map(([k]) => k)
  brand = { logo: logo || ENV_BRAND.logo, favicon: favicon || ENV_BRAND.favicon, name: name || ENV_BRAND.name }
  if (brand.name) document.title = `${brand.name} Admin`
  const icon = document.querySelector('link[rel="icon"]')
  if (icon && brand.favicon) icon.href = brand.favicon
  listeners.forEach((cb) => cb())
}

/** Fetch the owner's theme (public betting-config) and apply + remember it. */
export async function loadSiteTheme() {
  try {
    // Plain fetch: the public endpoint needs no token, and the api client's
    // 401 / loader handling has no business here.
    const res = await fetch(`${API_BASE_URL}/user/betting-config`)
    const t = (await res.json())?.theme || {}
    const abs = (path) => (path ? `${API_BASE_URL}${path}` : '')
    const look = { colors: t.colors || {}, name: t.name || '', logo: abs(t.logo), favicon: abs(t.favicon) }
    paint(look)
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(look)) } catch { /* private mode */ }
  } catch {
    // API down: keep the cached / built-in look.
  }
}

if (typeof document !== 'undefined') {
  try { paint(JSON.parse(localStorage.getItem(CACHE_KEY)) || {}) } catch { /* no cache */ }
}
